// © Microsoft Corporation. All rights reserved.

import { applyFontWeightNormal, applyFontWeightSemiBold, applyTypeRampConfig, getLineHeight, neutralFillStealthRest, neutralForegroundHover } from "@microsoft/fast-components-styles-msft";
import { applyLocalizedProperty, applyMaxLines, toPx } from "@microsoft/fast-jss-utilities";

import { ComponentStyles } from "@microsoft/fast-jss-manager";
import { DesignSystem } from "@microsoft/fast-components-styles-msft";
import { DialogArticleCardClassNameContract } from "./DialogArticleCard.classnames";

const ArticleCardImageBorderRadius = 2;
const RelatedCardHeight = 100;
const RelatedCardImageSize = 100;
const HeadlineTextMargin = 16;

const borderColor = "rgba(0,0,0, 0.05)";
const DialogArticleCardTypeRamp = "t7";

/**
 * The DialogArticleCard styles
 * @type {ComponentStyles<DialogArticleCardClassNameContract, DesignSystem>}
 */
export const DialogArticleCardStyles: ComponentStyles<DialogArticleCardClassNameContract, DesignSystem> = (config: DesignSystem) => {
    return {
        articleCard: {
            background: "none",
            "border-bottom": `1px solid ${borderColor}`,
            cursor: "pointer",
            display: "flex",
            padding: "15px 0",
            position: "relative"
        },
        articleCard_content: {
            display: "flex",
            "text-decoration": "none",
            "&:hover": {
                "& $articleCard_headline": {
                    color: neutralForegroundHover(neutralFillStealthRest),
                    "text-decoration": "underline"
                },
                "& $articleCard_imageOverlay": {
                    opacity: 0.1
                }
            }
        },
        articleCard_imageWrapper: {
            position: "relative",
            "& $articleCard_image": {
                height: toPx(RelatedCardImageSize)
            }
        },
        articleCard_image: {
            "border-radius": toPx(ArticleCardImageBorderRadius),
            "object-fit": "cover",
            width: toPx(RelatedCardImageSize)
        },
        articleCard_imageOverlay: {
            height: "100%",
            opacity: "0",
            position: "absolute",
            top: "0",
            width: "100%"
        },
        articleCard_textWrapper: {
            "box-sizing": "border-box",
            flex: "1",
            height: toPx(RelatedCardHeight),
            padding: applyLocalizedProperty(`0 0 0 ${toPx(HeadlineTextMargin)}`, `0 ${toPx(HeadlineTextMargin)} 0 0`, config.direction)
        },
        articleCard_headline: {
            "& $articleCard_headlineText": {
                ...applyFontWeightSemiBold(),
                ...applyMaxLines(3, getLineHeight(DialogArticleCardTypeRamp)),
                ...applyTypeRampConfig(DialogArticleCardTypeRamp)
            }
        },
        articleCard_headlineText: {
            "padding-top": "3px"
        },
        articleCard_headlineMask: {},
        articleCard_provider: {
            bottom: "23px",
            display: "inline-flex",
            position: "absolute",
            "& $articleCard_providerImage": {
                height: "16px"
            },
            "& $articleCard_providerTitle": {
                "line-height": "16px"
            }
        },
        articleCard_providerImage: {
            display: "inline-block",
            [applyLocalizedProperty("padding-right", "padding-left", config.direction)]: "8px"
        },
        articleCard_providerTitle: {
            display: "inline-block",
            "vertical-align": "top",
            ...applyFontWeightNormal()
        },
        articleCard_noImage: {
            "& $articleCard_textWrapper": {
                height: "auto",
                "text-align": applyLocalizedProperty("left", "right", config.direction)
            },
            "& $articleCard_provider": {
                "margin-top": "15px",
                position: "static"
            },
            "& $articleCard_headlineText": {
                "padding-top": "0"
            }
        }
    };
};